import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Alert,
  Button,
  IconButton,
  Typography,
  Fade,
  Autocomplete,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Delete, CloudUpload } from "@mui/icons-material";
import RichTextEditor from "../RichTextEditor/RichTextEditor";
import "./BlogDialog.scss";

const BlogDialog = ({ open, onClose, onSubmit, initialData, isEditing }) => {
  const initialFormData = {
    postType: "article",
    title: "",
    content: "",
    description: "",
    section: "",
    media: [],
    categories: [],
    tags: [],
  };

  const [formData, setFormData] = useState(initialFormData);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [categoryFeedback, setCategoryFeedback] = useState("");
  const [tagFeedback, setTagFeedback] = useState("");
  const [isLoading, setIsLoading] = useState(false); // New loading state

  // Dynamic categories and tags state
  const [dynamicCategories, setDynamicCategories] = useState([
    "Leadership",
    "Management",
    "Workplace Culture",
    "Communication",
    "Professional Growth",
    "Feedback & Performance",
    "Trust & Transparency",
    "Decision-Making",
    "Technology",
    "Innovation",
    "Startups",
    "AI & Future of Work",
    "Personal Development",
    "Team Dynamics",
    "Organizational Growth",
    "Employee Engagement",
  ]);

  const [dynamicTags, setDynamicTags] = useState([
    "Tech",
    "AI",
    "Machine Learning",
    "HealthTech",
    "EdTech",
    "Startups",
    "Productivity",
    "Innovation",
    "Research",
    "360-Degree Feedback",
    "Confidentiality",
    "Transparency",
    "Trust",
    "Leadership Presence",
    "Executive Development",
    "Performance Reviews",
    "Follow-Up",
    "Accountability",
    "Legal & Ethical Issues",
    "Feedback Culture",
    "Organizational Trust",
    "Inclusion",
    "Employee Growth",
    "Workplace Communication",
  ]);

  // Validation constants
  const MAX_TITLE_LENGTH = 100;
  const MIN_CONTENT_LENGTH = 50;
  const MAX_CATEGORIES = 5;
  const MAX_TAGS = 10;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
  const MAX_CATEGORY_LENGTH = 50;
  const MAX_TAG_LENGTH = 30;

  // Load initial data if editing
  useEffect(() => {
    if (open) {
      if (isEditing && initialData) {
        setFormData({
          postType: initialData.postType || "article",
          title: initialData.title || "",
          content: initialData.content || "",
          description: initialData.description || "",
          section: initialData.section || "",
          media: initialData.media || [],
          categories: initialData.categories || [],
          tags: initialData.tags || [],
        });
        // Add initialData categories/tags to dynamic lists if not already present
        if (initialData.categories) {
          setDynamicCategories((prev) => [
            ...new Set([...prev, ...initialData.categories]),
          ]);
        }
        if (initialData.tags) {
          setDynamicTags((prev) => [...new Set([...prev, ...initialData.tags])]);
        }
      } else {
        // Reset to initial state for creating a new post
        setFormData(initialFormData);
        setError("");
        setSuccess("");
        setCategoryFeedback("");
        setTagFeedback("");
      }
    }
  }, [open, isEditing, initialData]);

  // Input sanitization
  const sanitizeInput = (input) => {
    return input.replace(/[<>]/g, "").trim(); // Remove potential XSS characters
  };

  // Input handler with validation
  const handleInputChange = (e) => {
    if (isLoading) return; // Prevent changes during loading
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);

    if (name === "title" && sanitizedValue.length > MAX_TITLE_LENGTH) {
      setError(`Title cannot exceed ${MAX_TITLE_LENGTH} characters`);
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
    setError("");
  };

  // Content editor handler with validation
  const handleContentEditorChange = (newContent) => {
    if (isLoading) return; // Prevent changes during loading
    const sanitizedContent = sanitizeInput(newContent);
    setFormData((prev) => ({ ...prev, content: newContent }));
    if (sanitizedContent.length < MIN_CONTENT_LENGTH) {
      setError(`Content must be at least ${MIN_CONTENT_LENGTH} characters`);
    } else {
      setError("");
    }
  };

  // Categories handler with validation
  const handleCategoriesChange = (event, newValue) => {
    if (isLoading) return; // Prevent changes during loading
    const sanitizedCategories = newValue
      .map((category) => sanitizeInput(category))
      .filter(
        (category) =>
          category &&
          category.length <= MAX_CATEGORY_LENGTH &&
          !dynamicCategories.includes(category)
      );

    if (newValue.length > MAX_CATEGORIES) {
      setError(`Maximum ${MAX_CATEGORIES} categories allowed`);
      return;
    }

    if (sanitizedCategories.length > 0) {
      setDynamicCategories((prev) => [...new Set([...prev, ...sanitizedCategories])]);
      setCategoryFeedback(
        `Added new categor${sanitizedCategories.length > 1 ? "ies" : "y"}: ${sanitizedCategories.join(", ")}`
      );
      setTimeout(() => setCategoryFeedback(""), 3000);
    }

    setFormData((prev) => ({
      ...prev,
      categories: newValue.filter(
        (category) => category.trim() && category.length <= MAX_CATEGORY_LENGTH
      ),
    }));
    setError("");
  };

  // Tags handler with validation
  const handleTagsChange = (event, newValue) => {
    if (isLoading) return; // Prevent changes during loading
    const sanitizedTags = newValue
      .map((tag) => sanitizeInput(tag))
      .filter(
        (tag) => tag && tag.length <= MAX_TAG_LENGTH && !dynamicTags.includes(tag)
      );

    if (newValue.length > MAX_TAGS) {
      setError(`Maximum ${MAX_TAGS} tags allowed`);
      return;
    }

    if (sanitizedTags.length > 0) {
      setDynamicTags((prev) => [...new Set([...prev, ...sanitizedTags])]);
      setTagFeedback(
        `Added new tag${sanitizedTags.length > 1 ? "s" : ""}: ${sanitizedTags.join(", ")}`
      );
      setTimeout(() => setTagFeedback(""), 3000);
    }

    setFormData((prev) => ({
      ...prev,
      tags: newValue.filter((tag) => tag.trim() && tag.length <= MAX_TAG_LENGTH),
    }));
    setError("");
  };

  // Media validation
  const validateMedia = (file) => {
    if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
      return "Only images and videos are allowed";
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File "${file.name}" exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`;
    }
    return null;
  };

  // Drag & drop upload with validation
  const handleDrop = async (e) => {
    if (isLoading) return; // Prevent uploads during loading
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    const errors = files.map(validateMedia).filter((error) => error !== null);

    if (errors.length > 0) {
      setError(errors.join("; "));
      return;
    }

    await addMedia(files);
  };

  // File input with validation
  const handleFileInput = async (e) => {
    if (isLoading) return; // Prevent uploads during loading
    const files = Array.from(e.target.files);
    const errors = files.map(validateMedia).filter((error) => error !== null);

    if (errors.length > 0) {
      setError(errors.join("; "));
      return;
    }

    await addMedia(files);
  };

  const addMedia = async (files) => {
    const newMedia = files.map((file) => ({
      type: file.type.startsWith("image/") ? "image" : "video",
      url: URL.createObjectURL(file),
      file,
      caption: "",
    }));
    setFormData((prev) => ({
      ...prev,
      media: [...prev.media, ...newMedia],
    }));
    setError("");
  };

  const handleDeleteMedia = (url) => {
    if (isLoading) return; // Prevent deletions during loading
    setFormData((prev) => ({
      ...prev,
      media: prev.media.filter((m) => m.url !== url),
    }));
  };

  const handleMediaCaptionChange = (url, caption) => {
    if (isLoading) return; // Prevent caption changes during loading
    const sanitizedCaption = sanitizeInput(caption);
    setFormData((prev) => ({
      ...prev,
      media: prev.media.map((m) =>
        m.url === url ? { ...m, caption: sanitizedCaption } : m
      ),
    }));
  };

  // Submit with enhanced validation and loading state
   
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return; // Prevent multiple submissions

    // Required fields validation
    if (!formData.content.trim()) {
      setError("Content is required");
      return;
    }
    if (formData.content.length < MIN_CONTENT_LENGTH) {
      setError(`Content must be at least ${MIN_CONTENT_LENGTH} characters`);
      return;
    }
    if (formData.postType === "article" && !formData.title.trim()) {
      setError("Title is required for articles");
      return;
    }
    if (formData.postType === "article" && formData.title.length > MAX_TITLE_LENGTH) {
      setError(`Title cannot exceed ${MAX_TITLE_LENGTH} characters`);
      return;
    }
    if (formData.categories.length === 0) {
      setError("At least one category is required");
      return;
    }
    if (formData.tags.length === 0) {
      setError("At least one tag is required");
      return;
    }
    if (!formData.section) {
      setError("Choose one publishing section");
      return;
    }

    setIsLoading(true); // Set loading state
    const fd = new FormData();
    fd.append("postType", formData.postType);
    fd.append("title", formData.title);
    fd.append("content", formData.content);
    fd.append("description", formData.description);
    fd.append("section", formData.section);
    fd.append("status", "published");
    fd.append("channels", JSON.stringify(["leaderbridge"]));
    fd.append("categories", JSON.stringify(formData.categories));
    fd.append("tags", JSON.stringify(formData.tags));

    const captions = [];
    formData.media.forEach((m) => {
      if (m.file) {
        fd.append("media", m.file);
        captions.push(m.caption || "");
      } else {
        fd.append(
          "media",
          JSON.stringify({
            type: m.type,
            url: m.url,
            caption: m.caption || "",
          })
        );
      }
    });

    if (captions.length > 0) {
      fd.append("captions", JSON.stringify(captions));
    }

    try {
      await onSubmit(fd);
      setSuccess(
        isEditing ? "Post updated successfully" : "Post created successfully"
      );
      setError("");

      // Reset cleanly only when creating new post
      if (!isEditing) {
        setFormData({
          postType: "article",
          title: "",
          content: "",
          description: "",
          section: "",
          media: [],
          categories: [],
          tags: [],
        });
      }
    } catch (err) {
      setError(err.message || "Failed to submit post");
      setSuccess("");
    } finally {
      setIsLoading(false); // Clear loading state
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose} // Disable close during loading
      fullWidth
      className="blog-dialog"
      TransitionComponent={Fade}
      transitionDuration={400}
      PaperProps={{
        style: {
          margin: 8,
          width: "calc(100% - 16px)",
        },
      }}
    >
      <DialogTitle className="dialog-title">
        {isEditing ? "Edit Your Post" : "Create a New Post"}
      </DialogTitle>

      <DialogContent className="dialog-content">
        <Fade in={!!(error || success || categoryFeedback || tagFeedback)} timeout={300}>
          <Box>
            {error && <Alert severity="error" className="alert">{error}</Alert>}
            {success && <Alert severity="success" className="alert">{success}</Alert>}
            {categoryFeedback && (
              <Alert severity="success" className="alert">
                {categoryFeedback}
              </Alert>
            )}
            {tagFeedback && (
              <Alert severity="success" className="alert">
                {tagFeedback}
              </Alert>
            )}
          </Box>
        </Fade>

        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {isEditing && initialData && !initialData.section && (
          <Alert severity="warning" className="alert">
            This legacy article needs editorial review. Choose a publishing section before updating its publication state.
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth required>
            <InputLabel id="blog-publishing-section-label">Publishing Section</InputLabel>
            <Select
              labelId="blog-publishing-section-label"
              label="Publishing Section"
              value={formData.section}
              onChange={(event) => setFormData((current) => ({ ...current, section: event.target.value }))}
              disabled={isLoading}
            >
              <MenuItem value="practice">Practice</MenuItem>
              <MenuItem value="method">Method</MenuItem>
              <MenuItem value="foundations">Foundations</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Alert severity="info" className="alert">
          This article will be published to LeaderBridge Ideas when you submit the form.
        </Alert>

        <Typography variant="subtitle1" className="section-label">
          Categories (Max {MAX_CATEGORIES})
        </Typography>
        <Autocomplete
          multiple
          freeSolo
          options={dynamicCategories}
          value={formData.categories}
          onChange={handleCategoriesChange}
          disabled={isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select or Add Categories"
              variant="outlined"
              className="form-input"
              placeholder="Type and press Enter to add new categories"
              helperText={`Max ${MAX_CATEGORIES} categories, ${MAX_CATEGORY_LENGTH} characters each`}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option}
                {...getTagProps({ index })}
                className="category-chip"
              />
            ))
          }
        />

        <Typography variant="subtitle1" className="section-label">
          Tags (Max {MAX_TAGS})
        </Typography>
        <Autocomplete
          multiple
          freeSolo
          options={dynamicTags}
          value={formData.tags}
          onChange={handleTagsChange}
          disabled={isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select or Add Tags"
              variant="outlined"
              className="form-input"
              placeholder="Type and press Enter to add new tags"
              helperText={`Max ${MAX_TAGS} tags, ${MAX_TAG_LENGTH} characters each`}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option}
                {...getTagProps({ index })}
                className="tag-chip"
              />
            ))
          }
        />

        <Typography variant="subtitle1" className="section-label">
          Add Media (Max {MAX_FILE_SIZE / (1024 * 1024)}MB per file)
        </Typography>
        <Box
          className={`drag-drop-zone ${dragOver ? "drag-over" : ""}`}
          onDrop={handleDrop}
          onDragOver={(e) => {
            if (isLoading) return;
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
        >
          <CloudUpload className="upload-icon" />
          <Typography variant="body1" className="drag-drop-text">
            Drag & drop images or videos <br /> or <b>click to browse</b> (max {MAX_FILE_SIZE / (1024 * 1024)}MB)
          </Typography>
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileInput}
            className="file-input"
            disabled={isLoading}
          />
        </Box>

        <Box className="media-preview">
          {formData.media.map((media, index) => (
            <Box key={index} className="media-item">
              {media.type === "image" ? (
                <img
                  src={media.url}
                  alt="Preview"
                  className="media-preview-image"
                />
              ) : (
                <video src={media.url} controls className="media-preview-video" />
              )}
              <TextField
                label="Caption"
                value={media.caption || ""}
                onChange={(e) =>
                  handleMediaCaptionChange(media.url, e.target.value)
                }
                className="media-caption"
                size="small"
                variant="outlined"
                placeholder="Add a caption"
                disabled={isLoading}
              />
              <IconButton
                onClick={() => handleDeleteMedia(media.url)}
                className="delete-media-btn"
                disabled={isLoading}
              >
                <Delete />
              </IconButton>
            </Box>
          ))}
        </Box>

        {formData.postType === "article" && (
          <TextField
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            fullWidth
            required
            className="form-input"
            variant="outlined"
            placeholder="Craft a captivating title"
            helperText={`Max ${MAX_TITLE_LENGTH} characters`}
            error={formData.title.length > MAX_TITLE_LENGTH}
            disabled={isLoading}
          />
        )}

        <TextField
          label="Short Summary"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          fullWidth
          multiline
          minRows={2}
          className="form-input"
          variant="outlined"
          placeholder="A concise introduction shown on the Ideas card"
          disabled={isLoading}
        />

        <Typography variant="subtitle1" className="section-label">
          Write Your Content (Min {MIN_CONTENT_LENGTH} characters)
        </Typography>
        <RichTextEditor
          content={formData.content}
          setContent={handleContentEditorChange}
          disabled={isLoading}
        />
      </DialogContent>

      <DialogActions className="dialog-actions">
        <Button onClick={onClose} className="cancel-btn" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          className="submit-btn"
          disabled={
            isLoading ||
            !formData.content.trim() ||
            formData.content.length < MIN_CONTENT_LENGTH ||
            (formData.postType === "article" && !formData.title.trim()) ||
            formData.categories.length === 0 ||
            formData.tags.length === 0 ||
            !formData.section
          }
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            isEditing ? "Save Changes" : "Publish Post"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlogDialog;
